import {
  changeEventSchema,
  changeHistoryResponseSchema,
  initiativeDetailResponseSchema,
  inputRequestListResponseSchema,
  inputRequestSchema,
  type ChangeEvent,
  type InitiativeDetailResponse,
  type InputRequestDto,
} from '@complan/contracts';
import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { z } from 'zod';
import { apiFetch } from '@/shared/api/client';

/**
 * Data wiring for the Detail_Panel (task 23.4).
 *
 * Three server reads back the panel, each consumed exactly as the read area
 * built it — no projection or pagination is re-implemented here:
 *
 * - **The detail projection** ({@link useInitiativeDetail}) is `GET
 *   /api/initiatives/:id`. The server already withholds fields the reader may
 *   not see (they are simply absent, no marker) and folds the capability-gated
 *   `availableActions` into the body, so the client renders identity, timing,
 *   classification, governance, milestones, dependencies and reference links
 *   straight from the parsed payload and gates controls off `availableActions`.
 * - **The change history** ({@link useChangeHistory}) is `GET
 *   /api/initiatives/:id/history`, paged at 25, reverse chronological (R12.5).
 * - **The Input_Requests** ({@link useInputRequests}) is `GET
 *   /api/initiatives/:id/input-requests`, paged at 25, reverse chronological
 *   (R12.6).
 *
 * The two collections use {@link useInfiniteQuery} so the "load the next 25"
 * control (R12.5, R12.6) is a `fetchNextPage`, and the total the server reports
 * (readable rows only, no gap marker) drives whether another page exists. Both
 * parse the wire body through the contracts schema so a drifted shape fails at
 * the boundary rather than mis-rendering.
 */

/** The Detail_Panel page size for both paginated collections (R12.5, R12.6). */
export const HISTORY_PAGE_SIZE = 25;

/** Cache keys for the Detail_Panel reads, kept local so the shared key map stays untouched. */
export const detailQueryKeys = {
  detail: (initiativeId: string) => ['initiative-detail', initiativeId] as const,
  history: (initiativeId: string) => ['initiative-history', initiativeId] as const,
  inputRequests: (initiativeId: string) => ['initiative-input-requests', initiativeId] as const,
} as const;

/** One page of a paginated Detail_Panel collection. */
type PagedResult<TItem> = {
  readonly items: readonly TItem[];
  readonly totalCount: number;
  readonly limit: number;
  readonly offset: number;
};

/** Fetches the Detail_Panel projection for an Initiative (R12.1). */
export async function fetchInitiativeDetail(
  initiativeId: string,
): Promise<InitiativeDetailResponse> {
  const body = await apiFetch<unknown>(`/api/initiatives/${initiativeId}`);
  return initiativeDetailResponseSchema.parse(body);
}

/** React Query hook for the Detail_Panel projection. Disabled until an id is known. */
export function useInitiativeDetail(
  initiativeId: string,
): UseQueryResult<InitiativeDetailResponse, unknown> {
  return useQuery({
    queryKey: detailQueryKeys.detail(initiativeId),
    queryFn: () => fetchInitiativeDetail(initiativeId),
    enabled: initiativeId !== '',
  });
}

/**
 * Fetches one page of a paginated Detail_Panel collection at `path`, validating
 * each row through `itemSchema` and the envelope through the paged-response
 * schema so `totalCount` and the window are typed.
 */
async function fetchPage<TItem>(
  path: string,
  offset: number,
  responseSchema: z.ZodType<PagedResult<TItem>>,
): Promise<PagedResult<TItem>> {
  const params = new URLSearchParams({
    limit: String(HISTORY_PAGE_SIZE),
    offset: String(offset),
  });
  const body = await apiFetch<unknown>(`${path}?${params.toString()}`);
  return responseSchema.parse(body);
}

/**
 * The `getNextPageParam` shared by both collections: the offset of the next page
 * when the rows fetched so far are fewer than the readable total, else
 * `undefined` so `hasNextPage` becomes false and no further load control shows.
 * The total is the server's readable-only count, so a withheld row never leaves
 * a phantom "load more" (R17.5).
 */
function nextOffset<TItem>(
  lastPage: PagedResult<TItem>,
  allPages: readonly PagedResult<TItem>[],
): number | undefined {
  const loaded = allPages.reduce((sum, page) => sum + page.items.length, 0);
  return loaded < lastPage.totalCount ? loaded : undefined;
}

/** React Query infinite hook for the change history, paged at 25 (R12.5). */
export function useChangeHistory(
  initiativeId: string,
): UseInfiniteQueryResult<{ readonly pages: readonly PagedResult<ChangeEvent>[] }, unknown> {
  return useInfiniteQuery({
    queryKey: detailQueryKeys.history(initiativeId),
    initialPageParam: 0,
    enabled: initiativeId !== '',
    queryFn: ({ pageParam }) =>
      fetchPage<ChangeEvent>(
        `/api/initiatives/${initiativeId}/history`,
        pageParam,
        changeHistoryResponseSchema as unknown as z.ZodType<PagedResult<ChangeEvent>>,
      ),
    getNextPageParam: nextOffset,
  });
}

/** React Query infinite hook for the Input_Requests, paged at 25 (R12.6). */
export function useInputRequests(
  initiativeId: string,
): UseInfiniteQueryResult<{ readonly pages: readonly PagedResult<InputRequestDto>[] }, unknown> {
  return useInfiniteQuery({
    queryKey: detailQueryKeys.inputRequests(initiativeId),
    initialPageParam: 0,
    enabled: initiativeId !== '',
    queryFn: ({ pageParam }) =>
      fetchPage<InputRequestDto>(
        `/api/initiatives/${initiativeId}/input-requests`,
        pageParam,
        inputRequestListResponseSchema as unknown as z.ZodType<PagedResult<InputRequestDto>>,
      ),
    getNextPageParam: nextOffset,
  });
}

/** Flattens the infinite-query pages into one ordered list plus the readable total. */
export function flattenPages<TItem>(
  data: { readonly pages: readonly PagedResult<TItem>[] } | undefined,
): { readonly items: readonly TItem[]; readonly totalCount: number } {
  if (data === undefined) {
    return { items: [], totalCount: 0 };
  }
  const items = data.pages.flatMap((page) => page.items);
  const totalCount = data.pages.length > 0 ? (data.pages[data.pages.length - 1]?.totalCount ?? 0) : 0;
  return { items, totalCount };
}

// Re-exported so tests and callers reference the row schemas from one place.
export { changeEventSchema, inputRequestSchema };
export type { ChangeEvent, InputRequestDto, PagedResult };
